import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Printer, Download, Bluetooth, SlidersHorizontal } from 'lucide-react';
import { catalogAPI, purchasesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import LabelCanvas, { SIZE_MAP } from './LabelCanvas';
import LabelTemplate from './LabelTemplate';

const MM_TO_PX = 96 / 25.4;

const LABEL_SIZES = [
  { id: '50x75', label: '50×75 mm (Tall)' },
  { id: '40x30', label: '40×30 mm (Small)' },
  { id: '58x40', label: '58×40 mm (Medium)' },
  { id: '50x25', label: '50×25 mm (Narrow)' },
  { id: '80x50', label: '80×50 mm (Large)' },
];

const FORMAT_OPTIONS = [
  { id: 'CODE128', label: 'Code 128' },
  { id: 'EAN13',   label: 'EAN-13 (13 digits)' },
  { id: 'QR',      label: 'QR Code' },
];

const SIZE_MM = { '50x75': [50,75], '40x30': [40,30], '58x40': [58,40], '50x25': [50,25], '80x50': [80,50] };

const emptyForm = () => ({
  catalogId:      null,
  productName:    '',
  weightLabel:    '',
  mrp:            '',
  barcodeValue:   '',
  barcodeFormat:  'CODE128',
  mfgDate:        '',
  expiryDate:     '',
  expiryLabelType: 'expiry',
  copies:         1,
  isNewProduct:   false,
});

// Render a LabelCanvas off-screen and return the root element
async function renderOffscreen(labelProps) {
  const { createRoot } = await import('react-dom/client');
  const area = document.createElement('div');
  area.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
  document.body.appendChild(area);
  const root = createRoot(area);
  root.render(<LabelCanvas {...labelProps} />);
  await new Promise(r => setTimeout(r, 450)); // wait for JsBarcode useEffect
  return { el: area.firstElementChild, cleanup: () => { root.unmount(); document.body.removeChild(area); } };
}

export default function LabelsTab() {
  const { storeProfile } = useAuth();
  const storeName = storeProfile?.shop_name || '';
  const template  = storeProfile?.label_template || null;

  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [showResults,   setShowResults]   = useState(false);

  const [form,              setForm]              = useState(emptyForm());
  const [suggestingBarcode, setSuggestingBarcode] = useState(false);
  const [suggestCount,      setSuggestCount]      = useState(0);

  const [queue,       setQueue]       = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);

  const [labelSize,     setLabelSize]     = useState('50x75');
  const [showStoreName, setShowStoreName] = useState(true);
  const [printing,      setPrinting]      = useState(false);
  const [showTemplate,  setShowTemplate]  = useState(false);

  // ── Search catalog ────────────────────────────────────────────────────────────
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await catalogAPI.getAll({ search: q, limit: 10 });
      setSearchResults(res.data?.items ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setShowResults(true);
    doSearch(q);
  };

  // ── Barcode suggestion (matches Quick Add Product logic exactly) ───────────────
  const doSuggestBarcode = async (baseForm = null, forceReset = false) => {
    setSuggestingBarcode(true);
    const count = forceReset ? 0 : suggestCount;
    const isReset = !forceReset && count >= 10;
    const currentBarcode = baseForm?.barcodeValue || form.barcodeValue;
    const after = (!isReset && !forceReset && count > 0 && currentBarcode)
      ? parseInt(currentBarcode, 10) : undefined;
    try {
      const res = await purchasesAPI.suggestBarcode(after);
      setForm(prev => ({ ...prev, ...(baseForm || {}), barcodeValue: res.data.barcode }));
      setSuggestCount(isReset ? 1 : count + 1);
    } catch { /* silently ignore */ } finally {
      setSuggestingBarcode(false);
    }
  };

  const selectProduct = async (product) => {
    setShowResults(false);
    setSearchQuery(product.name);
    const needsBarcode = !product.barcode;
    const f = {
      ...emptyForm(),
      catalogId:    product.catalog_id,
      productName:  product.name || '',
      weightLabel:  product.pack_size || '',
      mrp:          product.mrp != null ? String(product.mrp) : '',
      barcodeValue: product.barcode || '',
    };
    setForm(f);
    if (needsBarcode) await doSuggestBarcode(f, true);
    else setSuggestCount(0);
  };

  const handleManualName = (name) => {
    setForm(prev => ({ ...prev, productName: name, catalogId: null, isNewProduct: true }));
  };

  const patchForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Queue management ──────────────────────────────────────────────────────────
  const addToQueue = () => {
    if (!form.productName.trim()) { toast.error('Enter a product name'); return; }
    const item = { ...form, _id: Date.now() };
    setQueue(prev => [...prev, item]);
    setSelectedIdx(null);
    setForm(emptyForm());
    setSearchQuery('');
    setSuggestCount(0);
  };

  const removeFromQueue = (idx) => {
    setQueue(prev => prev.filter((_, i) => i !== idx));
    setSelectedIdx(prev => prev === idx ? null : prev > idx ? prev - 1 : prev);
  };

  const updateQueueItem = (idx, key, value) => {
    setQueue(prev => prev.map((item, i) => i === idx ? { ...item, [key]: value } : item));
  };

  // ── Auto-save to Stock Master ─────────────────────────────────────────────────
  const saveToStockMaster = async (item) => {
    try {
      if (item.catalogId && item.barcodeValue) {
        await catalogAPI.update(item.catalogId, { barcode: item.barcodeValue });
      } else if (!item.catalogId && item.productName) {
        const packMatch = item.weightLabel.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
        await catalogAPI.create({
          name:      item.productName,
          barcode:   item.barcodeValue || undefined,
          mrp:       item.mrp ? parseFloat(item.mrp) : undefined,
          unit:      packMatch ? packMatch[2] : 'PCS',
          pack_size: packMatch ? item.weightLabel : undefined,
        });
        toast.success(`"${item.productName}" saved to Stock Master`);
      }
    } catch {
      toast.warning('Label printed — could not auto-save to Stock Master');
    }
  };

  // Build label props for a given item
  const labelProps = (item) => ({
    storeName,
    productName:     item.productName     || '',
    weightLabel:     item.weightLabel     || '',
    mrp:             item.mrp             || '',
    barcodeValue:    item.barcodeValue    || '',
    barcodeFormat:   item.barcodeFormat   || 'CODE128',
    mfgDate:         item.mfgDate         || '',
    expiryDate:      item.expiryDate      || '',
    expiryLabelType: item.expiryLabelType || 'expiry',
    labelSize,
    showStoreName,
  });

  // ── Browser print ─────────────────────────────────────────────────────────────
  const printBrowser = async () => {
    if (!queue.length) { toast.error('Queue is empty'); return; }

    const [wMM, hMM] = template
      ? [template.sticker_w_mm, template.sticker_h_mm]
      : SIZE_MM[labelSize];

    const { createRoot } = await import('react-dom/client');
    const area = document.createElement('div');
    area.id = 'kf-label-print-area';
    area.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
    document.body.appendChild(area);
    const root = createRoot(area);

    let labels;
    if (template) {
      const sW = template.sticker_w_mm * MM_TO_PX;
      const sH = template.sticker_h_mm * MM_TO_PX;
      const zX = template.zone_x_mm   * MM_TO_PX;
      const zY = template.zone_y_mm   * MM_TO_PX;
      const zW = template.zone_w_mm   * MM_TO_PX;
      const zH = template.zone_h_mm   * MM_TO_PX;
      labels = queue.flatMap((item, qi) =>
        Array.from({ length: item.copies }, (_, k) => (
          <div key={`${qi}-${k}`} style={{ width: sW, height: sH, position: 'relative', pageBreakAfter: 'always' }}>
            <div style={{ position: 'absolute', left: zX, top: zY, width: zW, height: zH, overflow: 'hidden' }}>
              <LabelCanvas {...labelProps(item)} customW={zW} customH={zH} />
            </div>
          </div>
        ))
      );
    } else {
      labels = queue.flatMap((item, qi) =>
        Array.from({ length: item.copies }, (_, k) => (
          <div key={`${qi}-${k}`} style={{ pageBreakInside: 'avoid', display: 'inline-block' }}>
            <LabelCanvas {...labelProps(item)} />
          </div>
        ))
      );
    }

    root.render(<>{labels}</>);
    await new Promise(r => setTimeout(r, 450));

    const style = document.createElement('style');
    style.id = 'kf-label-print-style';
    style.textContent = `
      @media print {
        @page { size: ${wMM}mm ${hMM}mm; margin: 0; }
        body > *:not(#kf-label-print-area) { display: none !important; }
        #kf-label-print-area { position: static !important; top: auto !important; left: auto !important; display: block !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();

    const cleanup = () => {
      root.unmount();
      document.body.removeChild(area);
      document.head.removeChild(style);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    for (const item of queue) await saveToStockMaster(item);
  };

  // ── BLE print ─────────────────────────────────────────────────────────────────
  const printBluetooth = async () => {
    if (!queue.length) { toast.error('Queue is empty'); return; }
    setPrinting(true);
    try {
      const { printLabelBluetooth } = await import('@/lib/printUtils');
      for (const item of queue) {
        const { el, cleanup } = await renderOffscreen(labelProps(item));
        try {
          for (let i = 0; i < item.copies; i++) {
            await printLabelBluetooth(el);
            if (i < item.copies - 1) await new Promise(r => setTimeout(r, 200));
          }
        } finally {
          cleanup();
        }
        await saveToStockMaster(item);
      }
      toast.success('Labels sent to printer');
    } catch (e) {
      if (!e?.message?.toLowerCase().includes('cancel'))
        toast.error('Bluetooth print failed: ' + (e?.message || 'unknown error'));
    } finally {
      setPrinting(false);
    }
  };

  // ── Download PNG ──────────────────────────────────────────────────────────────
  const downloadPng = async () => {
    const item = selectedIdx != null && queue[selectedIdx] ? queue[selectedIdx] : form;
    if (!item.productName && !item.mrp) { toast.error('Nothing to download'); return; }

    const { el, cleanup } = await renderOffscreen(labelProps(item));
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(el, {
        scale: 4, backgroundColor: '#ffffff', useCORS: true, logging: false,
      });
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `label-${item.productName || 'kf'}-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }, 'image/png', 0.95);
    } finally {
      cleanup();
    }

    if (item.productName) await saveToStockMaster(item);
  };

  // ── Preview ───────────────────────────────────────────────────────────────────
  const previewItem = selectedIdx != null && queue[selectedIdx]
    ? queue[selectedIdx]
    : form;

  const { w: labelW, h: labelH } = template
    ? { w: template.zone_w_mm * MM_TO_PX, h: template.zone_h_mm * MM_TO_PX }
    : (SIZE_MAP[labelSize] || SIZE_MAP['58x40']);
  const maxPreviewW  = 340;
  const previewScale = Math.min(2, maxPreviewW / labelW);

  const inputCls = 'w-full px-3 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="flex flex-col lg:flex-row gap-6">

      {/* ── Left: builder ─────────────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col">

        {/* Product form — flex-1 so it stretches to match the right column's total height */}
        <div className="bg-card border border-border rounded-lg p-4 space-y-3 flex-1">
          <h3 className="font-semibold text-sm text-foreground">Product</h3>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search catalog or type a new product name…"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 150)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {showResults && (searchResults.length > 0 || searching) && (
              <div className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-52 overflow-y-auto">
                {searching && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
                {searchResults.map(p => (
                  <button key={p.catalog_id} onMouseDown={() => selectProduct(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex justify-between items-center gap-2">
                    <span className="truncate font-medium">{p.name}</span>
                    <span className="text-muted-foreground shrink-0">
                      {p.mrp != null ? `₹${p.mrp}` : ''}{p.pack_size ? ` · ${p.pack_size}` : ''}
                    </span>
                  </button>
                ))}
                {!searching && searchResults.length === 0 && searchQuery.trim() && (
                  <button onMouseDown={() => { setShowResults(false); handleManualName(searchQuery); }}
                    className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-accent flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Add "{searchQuery}" as new product
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Row 1: Product Name | Weight/Qty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Product Name</label>
              <input value={form.productName} onChange={e => patchForm('productName', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Weight / Qty (e.g. 250 g)</label>
              <input value={form.weightLabel} onChange={e => patchForm('weightLabel', e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Row 2: MRP */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">MRP (₹)</label>
              <input type="number" value={form.mrp} onChange={e => patchForm('mrp', e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Row 3: Barcode | Barcode Format */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Barcode</label>
              <input value={form.barcodeValue} onChange={e => patchForm('barcodeValue', e.target.value)}
                placeholder="e.g. 1001" className={inputCls} />
              <button type="button" disabled={suggestingBarcode}
                onClick={() => doSuggestBarcode()}
                className="mt-0.5 text-[10px] text-primary underline underline-offset-2 hover:opacity-70 disabled:opacity-40 transition-opacity">
                {suggestingBarcode ? 'Suggesting…' : suggestCount === 0 ? 'Suggest code' : 'Suggest next available'}
              </button>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Barcode Format</label>
              <select value={form.barcodeFormat} onChange={e => patchForm('barcodeFormat', e.target.value)} className={inputCls}>
                {FORMAT_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 4: Mfg Date | Expiry / Best Before */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Mfg Date (optional)</label>
              <input type="date" value={form.mfgDate} onChange={e => patchForm('mfgDate', e.target.value)} className={inputCls} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted-foreground">
                  {form.expiryLabelType === 'bestbefore' ? 'Best Before (optional)' : 'Expiry Date (optional)'}
                </label>
                <div className="flex text-[10px] border border-input rounded overflow-hidden">
                  <button type="button"
                    onClick={() => patchForm('expiryLabelType', 'expiry')}
                    className={`px-1.5 py-0.5 transition-colors ${form.expiryLabelType === 'expiry' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}>
                    Expiry
                  </button>
                  <button type="button"
                    onClick={() => patchForm('expiryLabelType', 'bestbefore')}
                    className={`px-1.5 py-0.5 border-l border-input transition-colors ${form.expiryLabelType === 'bestbefore' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}>
                    Best Before
                  </button>
                </div>
              </div>
              <input type="date" value={form.expiryDate} onChange={e => patchForm('expiryDate', e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Row 5: Copies to Print (last field) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Copies to Print</label>
              <input type="number" min={1} max={500} value={form.copies}
                onChange={e => patchForm('copies', Math.max(1, parseInt(e.target.value) || 1))}
                className={inputCls} />
            </div>
          </div>

          <button onClick={addToQueue}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">
            <Plus className="h-4 w-4" /> Add to Print Queue
          </button>
        </div>

      </div>

      {/* ── Right: preview + settings + queue ─────────────────────────────── */}
      <div className="w-full lg:w-1/2 space-y-4">

        {/* Preview card with label settings inline at bottom */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold text-sm text-foreground mb-3">Preview</h3>
          <div className="flex items-center justify-center bg-muted/30 rounded-md p-6" style={{ minHeight: labelH * previewScale + 48 }}>
            <div style={{
              width:    labelW * previewScale,
              height:   labelH * previewScale,
              overflow: 'hidden',
              flexShrink: 0,
            }}>
              <div style={{
                transform:       `scale(${previewScale})`,
                transformOrigin: 'top left',
                width:           labelW,
                height:          labelH,
              }}>
                <LabelCanvas
                  {...labelProps(previewItem)}
                  {...(template ? { customW: labelW, customH: labelH } : {})}
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {selectedIdx != null && queue[selectedIdx]
              ? `Queue item ${selectedIdx + 1} of ${queue.length}`
              : 'Live preview of current form'}
          </p>

          {/* Label settings — lives here so changes reflect immediately above */}
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-2">Label Size</label>
              <div className="flex flex-wrap gap-2">
                {LABEL_SIZES.map(s => (
                  <button key={s.id} onClick={() => setLabelSize(s.id)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      labelSize === s.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:border-primary'
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showStoreName} onChange={e => setShowStoreName(e.target.checked)} className="rounded" />
              Show store name
            </label>

            {/* Custom sticker template */}
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Custom Sticker Template
                </span>
                <button
                  onClick={() => setShowTemplate(t => !t)}
                  className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity">
                  {showTemplate ? 'Close' : template ? 'Edit' : 'Set up'}
                </button>
              </div>
              {!showTemplate && (
                <p className="text-xs text-muted-foreground">
                  {template
                    ? `${template.sticker_w_mm}×${template.sticker_h_mm}mm sticker · ${template.zone_w_mm}×${template.zone_h_mm}mm print zone`
                    : 'No template — full KiranaFlow label printed'}
                </p>
              )}
              {showTemplate && <LabelTemplate onSaved={() => setShowTemplate(false)} />}
            </div>
          </div>
        </div>

        {/* Print Queue — always visible */}
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-sm text-foreground">
            Print Queue ({queue.length} item{queue.length !== 1 ? 's' : ''})
          </h3>

          {queue.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">
              No items yet — fill the form and click Add to Print Queue
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {queue.map((item, idx) => (
                  <div key={item._id}
                    className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer transition-colors ${
                      selectedIdx === idx ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                    }`}
                    onClick={() => setSelectedIdx(idx)}>
                    <div className="shrink-0" style={{ width: 54, height: 40, overflow: 'hidden', position: 'relative' }}>
                      <div style={{ transform: 'scale(0.35)', transformOrigin: 'top left', width: 154, height: 114 }}>
                        <LabelCanvas {...labelProps(item)} labelSize="40x30" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.productName}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.copies} cop{item.copies === 1 ? 'y' : 'ies'}{item.mrp ? ` · ₹${item.mrp}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={e => { e.stopPropagation(); updateQueueItem(idx, 'copies', Math.max(1, item.copies - 1)); }}
                        className="w-6 h-6 flex items-center justify-center rounded border border-border text-xs hover:bg-accent">−</button>
                      <span className="text-sm w-6 text-center">{item.copies}</span>
                      <button onClick={e => { e.stopPropagation(); updateQueueItem(idx, 'copies', item.copies + 1); }}
                        className="w-6 h-6 flex items-center justify-center rounded border border-border text-xs hover:bg-accent">+</button>
                      <button onClick={e => { e.stopPropagation(); removeFromQueue(idx); }}
                        className="ml-1 text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={printBrowser}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">
                  <Printer className="h-4 w-4" /> Print (Browser)
                </button>
                <button onClick={printBluetooth} disabled={printing}
                  className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">
                  <Bluetooth className="h-4 w-4" /> {printing ? 'Printing…' : 'Print via Bluetooth'}
                </button>
                <button onClick={downloadPng}
                  className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors">
                  <Download className="h-4 w-4" /> Download PNG
                </button>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
